import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Table, TableHead, TableRow,
  TableCell, TableBody, Divider, Button, CircularProgress,
} from '@mui/material';
import { Print, ArrowBack } from '@mui/icons-material';
import { prescriptionService, printSettingsService } from '../../services/api';

const PrescriptionPrintPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [prescription, setPrescription] = useState(null);
  const [printSettings, setPrintSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [prescRes, psRes] = await Promise.all([
          prescriptionService.getPrescriptionById(id),
          printSettingsService.getSettings(),
        ]);
        setPrescription(prescRes.data);
        setPrintSettings(psRes.data);
      } catch {
        setError('Prescription not found.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  // Auto-print once data is ready
  useEffect(() => {
    if (!loading && prescription) {
      const timer = setTimeout(() => window.print(), 500);
      return () => clearTimeout(timer);
    }
  }, [loading, prescription]);

  const calculateAge = (dob) => {
    if (!dob) return '';
    const birth = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    if (
      today.getMonth() < birth.getMonth() ||
      (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())
    ) age--;
    return age;
  };

  const formatDuration = (d) => {
    if (!d) return '';
    return `${d} ${d === '1' ? 'day' : 'days'}`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !prescription) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="error" sx={{ mb: 2 }}>{error || 'Prescription not found.'}</Typography>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)}>Go Back</Button>
      </Box>
    );
  }

  const patient  = prescription.patient;
  const doctor   = prescription.doctor;
  const mt = printSettings?.marginTop    ?? 10;
  const mb = printSettings?.marginBottom ?? 10;
  const ml = printSettings?.marginLeft   ?? 10;
  const mr = printSettings?.marginRight  ?? 10;
  const isPrePrinted = printSettings?.letterheadType === 'Pre-Printed Letterhead';

  const medicines      = prescription.prescriptionMedicines      || [];
  const advices        = prescription.prescriptionAdvices        || [];
  const investigations = prescription.prescriptionInvestigations || [];

  return (
    <Box>
      {/* On-screen controls — hidden during print */}
      <Box className="no-print" sx={{ p: 2, display: 'flex', gap: 2, borderBottom: '1px solid #e0e0e0', bgcolor: '#f5f5f5' }}>
        <Button startIcon={<ArrowBack />} variant="outlined" onClick={() => navigate(-1)}>
          Back
        </Button>
        <Button startIcon={<Print />} variant="contained" onClick={() => window.print()}>
          Print Again
        </Button>
        <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center', ml: 1 }}>
          Rx #{prescription.id} — {patient?.firstName} {patient?.lastName}
        </Typography>
      </Box>

      {/* Prescription Content */}
      <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }} className="print-area">

        {/* Hospital Header */}
        {!isPrePrinted && (
          <Box sx={{
            background: 'linear-gradient(135deg, #1565c0 0%, #2196f3 100%)',
            color: '#fff',
            textAlign: 'center',
            py: 1.5,
            px: 2,
            mb: 1.5,
            mx: -3,
            mt: -3,
          }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1.6rem', letterSpacing: 1, lineHeight: 1.2 }}>
              {printSettings?.hospitalName || 'Vedanta Hospital'}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', opacity: 0.9, mt: 0.3 }}>
              {printSettings?.address || ''}
              {printSettings?.address && printSettings?.phone ? ' | ' : ''}
              {printSettings?.phone || ''}
            </Typography>
          </Box>
        )}

        {/* Patient Info */}
        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f8fafc', px: 2, py: 1, mb: 1.5 }}>
          <Grid container alignItems="flex-start">
            <Grid item xs={4}>
              <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                <strong>Patient:</strong> {patient?.firstName} {patient?.lastName}
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                <strong>Age:</strong> {calculateAge(patient?.dateOfBirth)} yrs
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                <strong>Gender:</strong> {patient?.gender}
              </Typography>
              {prescription.weight && (
                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                  <strong>Weight:</strong> {prescription.weight} kg
                </Typography>
              )}
            </Grid>
            <Grid item xs={4} sx={{ textAlign: 'center' }}>
              {patient?.bloodGroup && (
                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                  <strong>Blood Group:</strong> {patient.bloodGroup}
                </Typography>
              )}
              {prescription.bp && (
                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                  <strong>BP:</strong> {prescription.bp} mmHg
                </Typography>
              )}
              {prescription.pulse && (
                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                  <strong>Pulse:</strong> {prescription.pulse} bpm
                </Typography>
              )}
              {(prescription.spo2 || prescription.spO2) && (
                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                  <strong>SpO₂:</strong> {prescription.spo2 || prescription.spO2}
                </Typography>
              )}
            </Grid>
            <Grid item xs={4} sx={{ textAlign: 'right' }}>
              <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                <strong>Rx No.:</strong> #{prescription.id}
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                <strong>Date:</strong> {new Date(prescription.date).toLocaleDateString('en-IN')}
              </Typography>
              {prescription.gestationalAge && (
                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                  <strong>Gest. Age:</strong> {prescription.gestationalAge}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Box>

        {/* Chief Complaint */}
        {prescription.chiefComplaint && (
          <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f8fafc', px: 2, py: 0.75, mb: 1.5 }}>
            <Typography variant="body2">
              <strong>Chief Complaint:</strong> {prescription.chiefComplaint}
            </Typography>
          </Box>
        )}

        {/* Diagnosis + Allergy */}
        {(prescription.provisionalDiagnosis || prescription.allergy) && (
          <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f8fafc', px: 2, py: 0.75, mb: 1.5, display: 'flex', gap: 3 }}>
            {prescription.provisionalDiagnosis && (
              <Typography variant="body2" sx={{ flex: 1 }}>
                <strong>Provisional Diagnosis:</strong> {prescription.provisionalDiagnosis}
              </Typography>
            )}
            {prescription.allergy && (
              <Typography variant="body2" sx={{ flex: 1 }}>
                <strong>Allergy:</strong> {prescription.allergy}
              </Typography>
            )}
          </Box>
        )}

        {/* Medicines */}
        {medicines.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ borderLeft: '3px solid #1565c0', pl: 1, mb: 0.75 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 1, color: '#1565c0' }}>
                Medicines Prescribed
              </Typography>
            </Box>
            <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.6, px: 1, fontSize: '0.82rem' } }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f0f4f8' }}>
                  <TableCell sx={{ fontWeight: 700, width: '40%' }}>Medicine</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '20%' }}>Dosage</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '20%' }}>Duration</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '20%' }}>Meal</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {medicines.map((pm, idx) => (
                  <TableRow key={pm.id} sx={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fafbfc' }}>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.82rem' }}>{pm.medicine?.name}</Typography>
                      {pm.medicine?.composition && pm.medicine.composition !== pm.medicine.name && (
                        <Typography sx={{ fontSize: '0.72rem', color: '#666', fontStyle: 'italic' }}>
                          {pm.medicine.composition}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{pm.dosage}</TableCell>
                    <TableCell>{formatDuration(pm.duration)}</TableCell>
                    <TableCell>{pm.mealPref || ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {/* Advices + Investigations */}
        {(advices.length > 0 || investigations.length > 0) && (
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {advices.length > 0 && (
              <Grid item xs={investigations.length > 0 ? 6 : 12}>
                <Box sx={{ borderLeft: '3px solid #1565c0', pl: 1, mb: 0.75 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 1, color: '#1565c0' }}>
                    Advice Given
                  </Typography>
                </Box>
                {advices.map((pa) => (
                  <Typography key={pa.id} sx={{ fontSize: '0.82rem', lineHeight: 1.8, pl: 0.5 }}>
                    • {patient?.hindiPrescription && pa.advice?.hindiText ? pa.advice.hindiText : pa.advice?.text}
                  </Typography>
                ))}
              </Grid>
            )}
            {investigations.length > 0 && (
              <Grid item xs={advices.length > 0 ? 6 : 12}>
                <Box sx={{ borderLeft: '3px solid #1565c0', pl: 1, mb: 0.75 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 1, color: '#1565c0' }}>
                    Investigations Advised
                  </Typography>
                </Box>
                {investigations.map((pi) => (
                  <Typography key={pi.id} sx={{ fontSize: '0.82rem', lineHeight: 1.8, pl: 0.5 }}>
                    • {pi.investigation?.name}
                  </Typography>
                ))}
              </Grid>
            )}
          </Grid>
        )}

        {/* Medical Notes */}
        {prescription.medicalNotes && (
          <Box sx={{ mt: 1.5 }}>
            <Box sx={{ borderLeft: '3px solid #1565c0', pl: 1, mb: 0.75 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 1, color: '#1565c0' }}>
                Medical Notes
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '0.82rem', lineHeight: 1.8, pl: 0.5 }}>
              {prescription.medicalNotes}
            </Typography>
          </Box>
        )}

        {/* Follow-up + Refer Doctor */}
        {(prescription.followUpDate || prescription.referDoctorName) && (
          <Box sx={{ mt: 1.5, display: 'flex', gap: 4 }}>
            {prescription.followUpDate && (
              <Box sx={{ flex: 1 }}>
                <Box sx={{ borderLeft: '3px solid #1565c0', pl: 1, mb: 0.75 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 1, color: '#1565c0' }}>
                    Follow-Up Date
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: '0.82rem', lineHeight: 1.8, pl: 0.5 }}>
                  {new Date(prescription.followUpDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}
                </Typography>
              </Box>
            )}
            {prescription.referDoctorName && (
              <Box sx={{ flex: 1 }}>
                <Box sx={{ borderLeft: '3px solid #1565c0', pl: 1, mb: 0.75 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 1, color: '#1565c0' }}>
                    Refer to Doctor
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: '0.82rem', lineHeight: 1.8, pl: 0.5 }}>
                  {prescription.referDoctorName}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Footer */}
        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 1.5, borderColor: '#1565c0' }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
            <Typography sx={{ fontSize: '0.72rem', color: '#888' }}>
              This prescription is computer generated.
            </Typography>
            <Box sx={{ textAlign: 'right' }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                Dr. {doctor?.firstName} {doctor?.lastName}
              </Typography>
              {doctor?.education && (
                <Typography sx={{ fontSize: '0.78rem', color: '#555' }}>{doctor.education}</Typography>
              )}
              {doctor?.medicalRegistrationNumber && (
                <Typography sx={{ fontSize: '0.78rem', color: '#555' }}>
                  Reg. No.: {doctor.medicalRegistrationNumber}
                </Typography>
              )}
              <Typography sx={{ fontSize: '0.82rem', mt: 6, borderTop: '1px solid #555', pt: 0.5, minWidth: 160, textAlign: 'center' }}>
                Signature
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <style>{`
        @media print {
          @page { margin: ${mt}mm ${mr}mm ${mb}mm ${ml}mm; }
          .no-print { display: none !important; }
        }
      `}</style>
    </Box>
  );
};

export default PrescriptionPrintPage;
